// OTA-safe installed-build warning helper.
//
// Compares the currently-installed iOS binary version against a minimum
// recommended version for remote-travel tracking reliability. The warning
// is advisory only — it never blocks tracking.
//
// We deliberately compare against the *native* `appVersion` (from
// @capacitor/app) and NOT the OTA `BUILD_ID`, because the failure modes the
// warning addresses (background heartbeat, durable native queue cadence,
// significant-change fallback) all live in the Swift LocationBG plugin and
// can only be upgraded by installing a newer App Store build.

import { useEffect, useState } from "react";
import { getDeviceStamp } from "./device-info";
import { isNative } from "./platform";

// Bump this when a new App Store build ships materially better tracking
// behavior. Travelers on anything older get the advisory banner.
export const MIN_RECOMMENDED_APP_VERSION = "5.9";

function parseVersion(v: string | undefined | null): number[] | null {
  if (!v || typeof v !== "string") return null;
  const parts = v.split(".").map((s) => Number(s.replace(/[^\d]/g, "")));
  if (parts.some((n) => !Number.isFinite(n))) return null;
  return parts;
}

function cmpVersion(a: number[], b: number[]): number {
  const len = Math.max(a.length, b.length);
  for (let i = 0; i < len; i++) {
    const ai = a[i] ?? 0;
    const bi = b[i] ?? 0;
    if (ai !== bi) return ai - bi;
  }
  return 0;
}

export type InstalledBuildState = {
  /** Native app version string, e.g. "5.9". Null on web or before resolved. */
  appVersion: string | null;
  /** True when running on iOS AND appVersion < MIN_RECOMMENDED_APP_VERSION. */
  outdated: boolean;
};

export function useInstalledBuildWarning(): InstalledBuildState {
  const [state, setState] = useState<InstalledBuildState>({ appVersion: null, outdated: false });
  useEffect(() => {
    if (!isNative()) return;
    let alive = true;
    void getDeviceStamp().then((s) => {
      if (!alive) return;
      const installed = parseVersion(s.appVersion);
      const min = parseVersion(MIN_RECOMMENDED_APP_VERSION);
      const outdated = !!(installed && min && cmpVersion(installed, min) < 0);
      setState({ appVersion: s.appVersion ?? null, outdated });
    });
    return () => { alive = false; };
  }, []);
  return state;
}
